import React from "react";
import { Helmet } from "react-helmet";
import Container from "react-bootstrap/Container";
import { useTranslation } from "react-i18next";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import SetNavigationRoute from "../../../components/navigation/SetNavigationRoute";
import { NAVIGATION_ROUTES } from "../../../components/dashboard/sidebar/utils/navigation-items";
import {
  DashboardContextValue,
  withDashboardContext,
} from "../../layouts/dashboard/DashboardContext";
import JobLocationHeader from "../../header/job-location-header";
import ClientHeaderList from "../../header/client-header-list";
import CardPlaceholder from "../../../components/dashboard/card-placeholder";
import EmptyPlaceholder from "../../../components/empty-placeholder";

import { connect } from "react-redux";
import { RootReducerState } from "../../../redux/reducer";
import { UserPayload } from "../../../graphql/types/models/auth";
import { UserRoles } from "../../../models/team-member";
import TeammateHeaderList from "../../header/teammate-header-list";
import {
  RouteComponentProps,
  generatePath,
  useHistory,
} from "react-router-dom";
import { useQuoteRequestsQuery } from "../../../hooks/queries/useQuoteRequestsQuery";
import QuoteRequestList from "../../../components/quote-requests/quote-request-list";
import {
  QuoteRequest,
  QuoteRequestSubcontractor,
} from "../../../graphql/types/models/quote-request";
import QuoteRequestCard from "../../quote-request/quote-request-card";
import { find, head } from "lodash";

type Params = {
  id: string;
  qr_id?: string;
  sub_id?: string;
};
type JobQuoteRequestsProps = RouteComponentProps<Params> &
  DashboardContextValue & { user: UserPayload | null };

const JobQuoteRequests: React.FC<JobQuoteRequestsProps> = ({
  navigationContext,
  match,
  user,
}) => {
  const isLocked = navigationContext?.job?.isLocked || false;
  const jobId = navigationContext?.job?._id || "";
  const { qr_id: quoteRequestId } = match.params;
  const { sub_id: subcontractorId } = match.params;
  const [selectedSubcontractorId, setSelectedSubcontractorId] = React.useState(
    ""
  );
  const [
    selectedRequest,
    setSelectedRequest,
  ] = React.useState<QuoteRequest | null>(null);

  const { t } = useTranslation();
  const history = useHistory();

  const {
    quoteRequests,
    getQuoteRequest,
    quoteRequest,
  } = useQuoteRequestsQuery({
    jobId,
  });

  React.useEffect(() => {
    const quoteRequest =
      find(quoteRequests, { _id: quoteRequestId || selectedRequest?._id }) ||
      head(quoteRequests);
    head(quoteRequests);
    const subcontractor =
      find(quoteRequest?.subcontractors, {
        _id: subcontractorId || selectedSubcontractorId,
      }) || head(quoteRequest?.subcontractors);
    if (quoteRequest && subcontractor) {
      handleSelect(quoteRequest, subcontractor);
    }
  }, [quoteRequests, quoteRequestId, subcontractorId]);

  const subcontractor = React.useMemo(() => {
    return quoteRequest?.subcontractors?.find(
      (s) => s._id === selectedSubcontractorId
    );
  }, [quoteRequest, selectedSubcontractorId]);

  const handleSelect = React.useCallback(
    (quoteRequest: QuoteRequest, subcontractor: QuoteRequestSubcontractor) => {
      setSelectedSubcontractorId(subcontractor._id);
      setSelectedRequest(quoteRequest);
      history.push(
        generatePath("/jobs/:id/quote-requests/:qr_id/subcontractor/:sub_id", {
          id: jobId,
          qr_id: quoteRequest._id,
          sub_id: subcontractor._id,
        })
      );
      getQuoteRequest({
        variables: {
          quoteRequestId: quoteRequest._id,
        },
      });
    },
    [getQuoteRequest, history, jobId]
  );

  return (
    <Container fluid className="m-0 p-0 h-100">
      <Helmet title={t("navigation.jobsSection.quoteRequests")} />
      <SetNavigationRoute
        routeId={NAVIGATION_ROUTES.JOBS_SECTION.QUOTE_REQUESTS}
      />
      <JobLocationHeader />
      <ClientHeaderList isReadonly={user?.role !== UserRoles.builderadmin} />
      <TeammateHeaderList isReadonly={user?.role !== UserRoles.builderadmin} />

      {quoteRequests.length ? (
        <Row className="h-100">
          <Col lg={4} sm={5} xs={12}>
            <QuoteRequestList
              title={t("quoteRequest.quoteRequests")}
              onSelect={handleSelect}
              quoteRequests={quoteRequests}
              selectedSubcontractorId={selectedSubcontractorId}
            />
          </Col>
          <Col lg={8} sm={7} xs={12} className="mh-100">
            {quoteRequest && subcontractor ? (
              <QuoteRequestCard
                quoteRequest={quoteRequest}
                subcontractor={subcontractor}
                jobId={jobId}
              />
            ) : (
              <CardPlaceholder />
            )}
          </Col>
        </Row>
      ) : (
        <EmptyPlaceholder message={t("quoteRequest.emptyPlaceholder")} />
      )}
    </Container>
  );
};

const mapStateToProps = (state: RootReducerState) => {
  return {
    user: state.authentication.user,
  };
};

export default connect(
  mapStateToProps,
  {}
)(withDashboardContext(JobQuoteRequests));
